import React, { useEffect, useState, useCallback, Dispatch } from 'react';
import { Dropdown } from 'components/reusable/Dropdown';
import { AreaType } from 'types/area';
import { objectIsEqual } from 'utils/helpers';
import { getAreas, changeArea, addArea, deleteArea } from 'data/areas';
import { EditAreas } from './EditAreas';
import { useAppDispatch } from 'hooks/hooks';
import { add, change, remove, replace } from 'hooks/areasSlice';

const EDIT_ID = 'NEW';

type Props = {
  styles: any;
  displayError: Dispatch<any>;
  confirmAction: Dispatch<any>;
};

export function Areas({ styles, displayError, confirmAction }: Props) {
  const dispatch = useAppDispatch();
  const [originalAreas, setOriginalAreas] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const updateAreas = useCallback(async () => {
    console.log('----------- GETTING AREAS DATA -----------');
    getAreas().then((data) => {
      setOriginalAreas(data);
      dispatch(replace({ areas: data }));
    });
  }, [dispatch]);

  const addNewArea = useCallback(() => {
    const newArea: AreaType = {
      id: EDIT_ID,
      name: '',
      icon: '',
      coordinates: undefined,
    };

    dispatch(add({ area: newArea }));
  }, [dispatch]);

  const cancelChanges = useCallback(
    (area: AreaType) => {
      if (area.id === EDIT_ID) {
        dispatch(remove({ id: area.id }));
      } else {
        const original = originalAreas.find((m: AreaType) => m.id === area.id);
        dispatch(change({ area: original }));
      }
    },
    [originalAreas, dispatch],
  );

  const saveArea = useCallback(
    (area: AreaType) => {
      if (area.id === EDIT_ID) {
        return addArea(area).then(updateAreas);
      }

      return changeArea(area).then(updateAreas);
    },
    [updateAreas],
  );

  const hasChanged = useCallback(
    (area: AreaType) => {
      if (originalAreas) {
        const original = originalAreas.find((o: AreaType) => o.id === area.id);
        return !objectIsEqual(original, area);
      }
      return false;
    },
    [originalAreas],
  );

  const actualDeleteArea = useCallback(
    (id: string) => {
      if (id === EDIT_ID) {
        dispatch(remove({ id }));
        return Promise.resolve();
      }

      return deleteArea(id)
        .then(updateAreas)
        .catch(() =>
          displayError('An error has occurred please try again later.'),
        );
    },
    [updateAreas, displayError, dispatch],
  );

  const confirmDelete = useCallback(
    (area: AreaType) => {
      confirmAction({
        title: 'Delete Area',
        name: area.name || 'this area',
        delete: () => actualDeleteArea(area.id),
      });
    },
    [confirmAction, actualDeleteArea],
  );

  useEffect(() => {
    updateAreas();
  }, [updateAreas]);

  return (
    <Dropdown
      title={'Areas'}
      isCollapsed={isCollapsed}
      setCollapsed={() => setIsCollapsed(!isCollapsed)}
    >
      <EditAreas
        styles={styles}
        hasChanged={hasChanged}
        addArea={addNewArea}
        deleteArea={confirmDelete}
        saveArea={saveArea}
        cancelChanges={cancelChanges}
        displayError={displayError}
      />
    </Dropdown>
  );
}
